import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { trackLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | err
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("err");
      setMsg("Моля, въведи валиден имейл.");
      return;
    }
    try {
      setStatus("loading");
      await axios.post(`${API}/newsletter`, { email });
      // Meta Lead event (browser + CAPI dedupe)
      trackLead({
        email,
        content_name: "Newsletter Signup",
        content_category: "newsletter",
      });
      setStatus("ok");
      setMsg("Записан си! Ще научиш пръв какво обявяваме на 25.10.");
      setEmail("");
    } catch (err) {
      setStatus("err");
      setMsg("Нещо се обърка. Опитай пак.");
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-20 sm:py-28 bg-[#0F2647] text-[#F5EBD9] border-y border-[#F5EBD9]/10 overflow-hidden"
    >
      <div className="absolute inset-0 sunset-glow opacity-70 pointer-events-none" />
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#F5892E]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
              Специално обявление · 25.10
            </span>
            <span className="h-px w-10 bg-[#F5892E]" />
          </div>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-4">
            На 25.10 обявяваме <span className="text-[#F5892E]">нещо специално</span>.
          </h2>
          <p className="text-base sm:text-lg text-[#F5EBD9]/70 max-w-[52ch] mx-auto mb-8">
            Остави имейла си, за да не го пропуснеш — ще научиш пръв, преди всички останали.
            Никакъв спам — само важното.
          </p>

          <form
            onSubmit={submit}
            data-testid="newsletter-form"
            className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
          >
            <div className="relative flex-1">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5EBD9]/40" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="твоят@имейл.бг"
                data-testid="newsletter-input"
                className="w-full pl-11 pr-4 py-3.5 bg-[#0B1F3D] border border-[#F5EBD9]/20 text-[#F5EBD9] placeholder:text-[#F5EBD9]/40 rounded-full focus:outline-none focus:border-[#F5892E] transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="newsletter-submit"
              className="btn-accent px-6 py-3.5 rounded-full uppercase tracking-widest text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {status === "loading"
                ? "Записваме..."
                : status === "ok"
                ? "Готово"
                : "Запиши ме"}
              {status === "ok" ? <Check className="w-4 h-4" strokeWidth={2.5} /> : <ArrowRight className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          </form>

          {msg && (
            <div
              data-testid="newsletter-msg"
              className={`mt-4 text-sm font-mono tracking-wide ${status === "ok" ? "text-[#E0B95C]" : "text-[#F5892E]"}`}
            >
              {msg}
            </div>
          )}

          <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5EBD9]/40">
            Това издание е разпродадено — следващото не чака.
          </div>
        </motion.div>
      </div>
    </section>
  );
}
